import { set } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

const combineDateWithTime = (dateSource, timeSource, timezone) => {
  if (timezone) {
    const d = toZonedTime(new Date(dateSource), timezone);
    const t = toZonedTime(new Date(timeSource), timezone);
    const combined = set(d, {
      hours: t.getHours(),
      minutes: t.getMinutes(),
      seconds: t.getSeconds()
    });
    return fromZonedTime(combined, timezone);
  }

  const d = new Date(dateSource);
  const t = new Date(timeSource);
  return set(d, { hours: t.getHours(), minutes: t.getMinutes(), seconds: t.getSeconds() });
};

const setDefaultTimes = (baseDate, hours, minutes, timezone) => {
  if (timezone) {
    const zoned = toZonedTime(new Date(baseDate), timezone);
    return fromZonedTime(set(zoned, { hours, minutes, seconds: 0 }), timezone);
  }
  return set(new Date(baseDate), { hours, minutes, seconds: 0 });
};

export const combineDateTime = (
  dateVal,
  timeVal,
  isDateRange = false,
  isTimeRange = false,
  timezone = null
) => {
  if (!dateVal && !timeVal) return null;

  // Special case: time range with single date (not date range)
  // dateVal = single date (e.g., Sept 22), timeVal = [startTime, endTime]
  if (isTimeRange && !isDateRange) {
    const baseDate = dateVal;
    const times = timeVal || [null, null];

    // If we have a base date but no times, create default times (00:00 - 23:59)
    if (baseDate && !times[0] && !times[1]) {
      return [
        setDefaultTimes(baseDate, 0, 0, timezone),
        setDefaultTimes(baseDate, 23, 59, timezone)
      ];
    }

    const combinedStart =
      baseDate && times[0]
        ? combineDateWithTime(baseDate, times[0], timezone)
        : baseDate || times[0];

    const combinedEnd =
      baseDate && times[1]
        ? combineDateWithTime(baseDate, times[1], timezone)
        : baseDate || times[1];

    return [combinedStart, combinedEnd];
  }

  // Date range or both date range and time range
  if (isDateRange || isTimeRange) {
    const dates = dateVal || [null, null];
    const times = timeVal || [null, null];

    // If we have dates but no times, create default times (00:00 - 23:59)
    if (dates[0] && !times[0] && !times[1]) {
      return [
        setDefaultTimes(dates[0], 0, 0, timezone),
        setDefaultTimes(dates[1] || dates[0], 23, 59, timezone)
      ];
    }

    const combinedStart =
      dates[0] && times[0]
        ? combineDateWithTime(dates[0], times[0], timezone)
        : dates[0] || times[0];

    const combinedEnd =
      dates[1] && times[1]
        ? combineDateWithTime(dates[1], times[1], timezone)
        : dates[1] || times[1];

    return [combinedStart, combinedEnd];
  }

  // Single date with optional time
  if (dateVal && timeVal) {
    return combineDateWithTime(dateVal, timeVal, timezone);
  }

  return dateVal || timeVal;
};
